import { useState, useEffect, useCallback } from 'react';
import { Box, Card, Title } from '@nimbus-ds/components';
import { DataList } from '@nimbus-ds/patterns';
import { ErrorScreen } from 'commons/components';
import { useToast, useModal } from 'commons/hooks';
import { useTranslationCustomers } from 'domains/Customers/hooks';
import SubscriptionsCancelModal from './CustomerSubscriptionsCancelModal';
import CustomerSubscriptionsCardSkeleton from './CustomerSubscriptionsCardSkeleton';
import CustomerSubscriptionRow from './CustomerSubscriptionsProductsCard/CustomerSubscriptionRow';
import { useCustomerSubscriptions } from '../../hooks';

interface CustomerSubscriptionsCardProps {
  customerId: string;
}

export default function CustomerSubscriptionsCard({
  customerId,
}: Readonly<CustomerSubscriptionsCardProps>) {
  const t = useTranslationCustomers('customerDetails.customerSubscriptions');
  const {
    subscriptions,
    isLoading: isLoadingSubscriptions,
    isError: isErrorSubscriptions,
    refetch: refetchSubscriptions,
    cancelSubscription,
    isCancelling,
    isCancelError,
  } = useCustomerSubscriptions(customerId);

  const { addToast } = useToast();
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<string>('');

  const [
    isShowCancelSubscriptionModal,
    openCancelSubscriptionModal,
    closeCancelSubscriptionModal,
  ] = useModal();

  const handleCancelSubscription = useCallback(async () => {
    await cancelSubscription({
      subscriptionId: selectedSubscriptionId,
    }).unwrap();
    closeCancelSubscriptionModal();
    addToast({
      label: t('cancelSuccess'),
      appearance: 'success',
    });
    setSelectedSubscriptionId('');
  }, [
    cancelSubscription,
    selectedSubscriptionId,
    closeCancelSubscriptionModal,
    addToast,
    t,
  ]);

  useEffect(() => {
    if (isCancelError) {
      addToast({
        label: t('cancelError'),
        appearance: 'danger',
      });
      closeCancelSubscriptionModal();
      setSelectedSubscriptionId('');
    }
  }, [
    isCancelError,
    addToast,
    t,
    closeCancelSubscriptionModal,
    refetchSubscriptions,
  ]);

  const [expandedSubscriptionIds, setExpandedSubscriptionIds] = useState<
    string[]
  >([]);

  const handleExpand = useCallback((id: string) => {
    setExpandedSubscriptionIds((prev) =>
      prev.includes(id) ? prev : [...prev, id],
    );
  }, []);
  const handleCollapse = useCallback((id: string) => {
    setExpandedSubscriptionIds((prev) => prev.filter((subId) => subId !== id));
  }, []);

  const onConfirmCancel = useCallback(async () => {
    if (!selectedSubscriptionId) return;
    await handleCancelSubscription();
  }, [selectedSubscriptionId, handleCancelSubscription]);

  if (isLoadingSubscriptions) return <CustomerSubscriptionsCardSkeleton />;
  if (!isErrorSubscriptions && subscriptions.length === 0) return null;

  return (
    <Card>
      <Card.Header>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Title as="h4">{t('title')}</Title>
        </Box>
      </Card.Header>

      <Card.Body>
        {isErrorSubscriptions ? (
          <ErrorScreen
            description={t('errorSubscriptions')}
            onRetry={refetchSubscriptions}
          />
        ) : (
          <DataList>
            {subscriptions.map((sub) => (
              <CustomerSubscriptionRow
                key={sub.id}
                sub={sub}
                expanded={expandedSubscriptionIds.includes(sub.id)}
                onExpand={() => handleExpand(sub.id)}
                onCollapse={() => handleCollapse(sub.id)}
                setSelectedSubscriptionId={setSelectedSubscriptionId}
                openCancelSubscriptionModal={openCancelSubscriptionModal}
              />
            ))}
          </DataList>
        )}

        <SubscriptionsCancelModal
          show={isShowCancelSubscriptionModal}
          isLoading={isCancelling}
          onDismiss={closeCancelSubscriptionModal}
          onConfirm={onConfirmCancel}
        />
      </Card.Body>
    </Card>
  );
}
